import React from "react";
import { Avatar } from "@mui/material";
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";

import ThisTheme from "../../../themes/RMSTheme";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";

const StyledBadge = styled(Badge)(({ theme, bgcolor = "" }) => ({
    "& .MuiBadge-badge": {
        height: 24,
        width: 24,
        borderRadius: "50%",
        backgroundColor: bgcolor || "#44b700",
        color: "#44b700",
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        border: `1px solid ${theme.palette.background.paper}`,
        padding: "0 4px",
    },
}));

export const avatarStyle = {
    backgroundColor: ThisTheme.palette.primary.main,
    color: "#FFF",
};

const RMSAvatar = React.forwardRef(function RMSTAvatar(props, ref) {
    const { type, disabled, badge, ...rest } = props;

    let display = (
        <div {...rest} ref={ref}>
            <IconAvatar disabled={disabled} type={type} />
        </div>
    );

    switch (badge) {
        case "completed":
        case "checked":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <CheckIcon
                            alt="Completed"
                            sx={{ fontSize: 16, color: "#FFFFFF" }}
                        />
                    }
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );

            break;

        case "attached":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <AttachFileIcon
                            alt="Associated"
                            sx={{ fontSize: 16, color: "#FFFFFF" }}
                        />
                    }
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );

            break;

        case "warning":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <PriorityHighIcon
                            alt="Warning"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#ff9800"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );

            break;

        case "review":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <AutorenewIcon
                            alt="Review"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#2196F3"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );
            break;

        case "review_self":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <AutorenewIcon
                            alt="Review"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#ff9800"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );
            break;

        case "person":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <PersonIcon
                            alt="Review"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#2196F3"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );

            break;

        case "closed":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <CloseIcon
                            alt="Closed"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#B31527"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );

            break;

        default:
            break;
    }

    return display;
});

const IconAvatar = ({ type = null, disabled = false }) => {
    const opacity = disabled ? 0.38 : 1;
    let icon = <AssignmentOutlinedIcon />;

    switch (type) {
        case "appraisee":
            icon = <PermIdentityOutlinedIcon />;
            break;

        case "appraisal":
            icon = <AssignmentOutlinedIcon />;
            break;

        default:
            break;
    }

    return (
        <Avatar
            aria-label="Avatar"
            style={{ ...avatarStyle, opacity: opacity }}
            sx={{ width: 52, height: 52, marginRight: "4px" }}
        >
            {icon}
        </Avatar>
    );
};

export default RMSAvatar;
